import React, { useState } from 'react';
import { ArrowUpRight, Mail, MapPin, Phone, Check } from 'lucide-react';
import Reveal from '../components/Reveal';
import { COMPANY, FAQ, SERVICES } from '../data/mock';
import { submitLead } from '../lib/api';

const BUDGETS = ['< £500k', '£500k – £2m', '£2m – £10m', '£10m – £30m', '£30m +'];

export default function Contact() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service: '', budget: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await submitLead(form);
      setSent(true);
    } catch (err) {
      setError('Sorry, something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--bg)]">
      <section className="pt-36 pb-16 border-b border-[var(--line)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> 008 — Contact</span>
          <Reveal>
            <h1 className="font-display text-7xl md:text-[150px] leading-[0.92] mt-6">Brief the <span className="font-display-italic text-[var(--coral)]">studio.</span></h1>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-8 mt-10">
            <p className="md:col-span-6 md:col-start-7 text-[18px] text-[var(--ink-mute)] leading-relaxed">
              Tell us a little about your project. A senior partner will read your note personally and reply within two working days.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-rs grid md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <div className="sticky top-32">
              <div className="font-display text-4xl md:text-5xl leading-tight">Merlin House,<br /><span className="font-display-italic text-[var(--coral)]">Telford.</span></div>
              <ul className="mt-10 space-y-5 text-[15px]">
                <li className="flex items-start gap-3"><MapPin size={16} className="mt-1 text-[var(--coral)]" /><span>{COMPANY.address}</span></li>
                <li className="flex items-center gap-3"><Phone size={16} className="text-[var(--coral)]" /><a href={`tel:${COMPANY.phoneTel}`} className="u-link">{COMPANY.phone}</a></li>
                <li className="flex items-center gap-3"><Mail size={16} className="text-[var(--coral)]" /><a href={`mailto:${COMPANY.email}`} className="u-link break-all">{COMPANY.email}</a></li>
              </ul>
              <div className="mt-10 overflow-hidden rounded-2xl border border-[var(--line)]">
                <iframe title="map" src="https://www.openstreetmap.org/export/embed.html?bbox=-2.45%2C52.65%2C-2.40%2C52.69&amp;layer=mapnik" className="w-full h-[300px]" loading="lazy" />
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {!sent ? (
              <form onSubmit={submit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <Field label="Your name" value={form.name} onChange={update('name')} required />
                  <Field label="Company" value={form.company} onChange={update('company')} />
                  <Field label="Email" type="email" value={form.email} onChange={update('email')} required />
                  <Field label="Phone" value={form.phone} onChange={update('phone')} />
                </div>
                <div>
                  <Label>Service of interest</Label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICES.map((s) => (
                      <button type="button" key={s.id} onClick={() => setForm({ ...form, service: s.title })} className={`px-5 py-3 rounded-full font-tag border transition-colors ${form.service === s.title ? 'bg-[var(--navy)] text-white border-[var(--navy)]' : 'border-[var(--line)] hover:border-[var(--navy)]'}`}>{s.title}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Indicative project value</Label>
                  <div className="flex flex-wrap gap-2">
                    {BUDGETS.map((b) => (
                      <button type="button" key={b} onClick={() => setForm({ ...form, budget: b })} className={`px-5 py-3 rounded-full font-tag border transition-colors ${form.budget === b ? 'bg-[var(--coral)] text-white border-[var(--coral)]' : 'border-[var(--line)] hover:border-[var(--coral)] hover:text-[var(--coral)]'}`}>{b}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Tell us about your project</Label>
                  <textarea required rows={6} value={form.message} onChange={update('message')} className="w-full bg-transparent border-b border-[var(--ink)]/30 focus:border-[var(--coral)] outline-none py-3 text-[16px] resize-none transition-colors" placeholder="Location, scope, sectors, programme, anything else we should know..." />
                </div>
                <div className="pt-4">
                  <button type="submit" className="pill pill-coral"><span>Send Brief</span><ArrowUpRight size={14} /></button>
                  <p className="mt-4 text-[12px] text-[var(--ink-mute)]">We'll never share your data. By submitting you agree to our privacy policy.</p>
                </div>
              </form>
            ) : (
              <div className="soft-card p-12 border border-[var(--coral)]/40">
                <div className="w-14 h-14 rounded-full bg-[var(--coral)]/10 text-[var(--coral)] flex items-center justify-center"><Check /></div>
                <h3 className="font-display text-5xl mt-6">Thank you, {form.name || 'friend'}.</h3>
                <p className="mt-4 text-[var(--ink-mute)] max-w-md">We've received your brief. A senior partner will read it personally and reply within two working days.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[var(--bg-soft)]">
        <div className="container-rs grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="section-index"><span className="coral-dot" /> FAQ</span>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">Common <span className="font-display-italic text-[var(--coral)]">questions.</span></h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            {FAQ.map((f, i) => (
              <div key={i} className="border-t border-[var(--line)]">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-6 py-6 text-left">
                  <span className="font-display text-2xl md:text-3xl">{f.q}</span>
                  <span className={`text-[var(--coral)] text-3xl transition-transform duration-500 ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ${openFaq === i ? 'max-h-60 pb-6' : 'max-h-0'}`}>
                  <p className="text-[15px] text-[var(--ink-mute)] leading-relaxed max-w-2xl">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Label({ children }) {
  return <div className="font-tag text-[var(--ink-mute)] mb-3">{children}</div>;
}
function Field({ label, ...props }) {
  return (
    <div>
      <Label>{label}</Label>
      <input {...props} className="w-full bg-transparent border-b border-[var(--ink)]/30 focus:border-[var(--coral)] outline-none py-3 text-[16px] transition-colors" />
    </div>
  );
}
